import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CartPage.css';
import { getAssetUrl } from '../config/api';

const CartPage = ({ cart, onUpdateQuantity, onRemoveFromCart }) => {
  const navigate = useNavigate();
  const [showTermsModal, setShowTermsModal] = useState(false);

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => {
      const price = typeof item.price === 'string' 
        ? parseFloat(item.price.replace(/[$,]/g, '')) 
        : parseFloat(item.price);
      return total + (price * item.quantity);
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const shipping = subtotal > 0 ? 0 : 0; // Free shipping
  const total = subtotal + shipping;

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <h1 className="cart-title">Shopping Bag</h1>
          <div className="empty-cart">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
            <p>Your shopping bag is empty</p>
            <button className="continue-shopping-btn" onClick={() => navigate('/products')}>
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <h1 className="cart-title">Shopping Bag</h1>
        <div className="cart-layout">
          {/* Cart Items */}
          <div className="cart-items">
            {cart.map((item) => {
              // Prefer cartImage (color-specific), then first default image, then gradient
              const displayImage = item.cartImage || (item.images && item.images.length > 0 ? item.images[0] : null);
              const imageStyle = displayImage
                ? { backgroundImage: `url(${getAssetUrl(displayImage)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                : { background: item.gradient };
              
              return (
                <div key={item.id || item._id} className="cart-item">
                  <div className="cart-item-image" style={imageStyle} />
                  <div className="cart-item-details">
                  <div className="cart-item-info">
                    <h3 className="cart-item-name">{item.name}</h3>
                    {item.categoryLabel && (
                      <p className="cart-item-code">{item.categoryLabel}</p>
                    )}
                    {item.selectedColor && (
                      <p className="cart-item-color">
                        <span
                          className="cart-color-swatch"
                          style={{ backgroundColor: item.selectedColorHex || '#ccc' }}
                        />
                        Color: {item.selectedColor}
                      </p>
                    )}
                    {item.selectedSize && (
                      <p className="cart-item-size">Size: {item.selectedSize}</p>
                    )}
                    <p className="cart-item-price">৳{typeof item.price === 'number' ? item.price.toFixed(2) : item.price}</p>
                  </div>
                  
                  <div className="cart-item-actions">
                    <div className="quantity-selector">
                      <button 
                        onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}>
                        +
                      </button>
                    </div>
                    
                    <button 
                      className="remove-item-btn"
                      onClick={() => onRemoveFromCart(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Cart Summary */}
          <div className="cart-summary">
            <h2 className="summary-title">Order Summary</h2>
            
            <div className="summary-row">
              <span>Subtotal ({cart.length} {cart.length === 1 ? 'item' : 'items'})</span>
              <span>৳{subtotal.toFixed(2)}</span>
            </div>
            
            <div className="summary-row">
              <span>Shipping</span>
              <span>{shipping === 0 ? 'Free' : `৳${shipping.toFixed(2)}`}</span>
            </div>
            
            <div className="summary-divider"></div>
            
            <div className="summary-row total">
              <span>Total</span>
              <span>৳{total.toFixed(2)}</span>
            </div>
            
            <button className="checkout-btn" onClick={() => navigate('/checkout')}>
              Proceed to Checkout
            </button>

            <p className="terms-note">
              By placing an order you agree to our{' '}
              <button className="terms-link" onClick={() => setShowTermsModal(true)}>
                Terms &amp; Conditions
              </button>
            </p>
            
            <p className="delivery-note">Complimentary Standard Delivery or Collect-in-Store.</p>
            
            <button className="continue-shopping-link" onClick={() => navigate('/products')}>
              Continue Shopping
            </button>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Return &amp; Refund Policy</h2>
              <button className="terms-modal-close" onClick={() => setShowTermsModal(false)}>&#x2715;</button>
            </div>
            <div className="terms-modal-body">
              <section>
                <h3>1. Returns &amp; Complaints</h3>
                <ul>
                  <li>All returns, exchanges, or complaints must be made at the time of delivery and in the presence of the delivery agent.</li>
                  <li>Issues such as receiving the wrong size or damaged products must be reported immediately to qualify for a return.</li>
                </ul>
              </section>

              <section>
                <h3>2. Condition of Returned Items</h3>
                <p>To be eligible for a return:</p>
                <ul>
                  <li>Items must be in their original condition.</li>
                  <li>All original tags, labels, and packaging must remain intact.</li>
                  <li>Items must show no signs of wear, use, washing, or alteration.</li>
                </ul>
              </section>

              <section>
                <h3>3. Inspection &amp; Refund Approval</h3>
                <ul>
                  <li>Once we receive and inspect the returned item, we will notify you regarding the approval or rejection of your refund.</li>
                  <li>If approved, your refund will be processed within a standard processing period (varies by payment method).</li>
                </ul>
              </section>

              <section>
                <h3>4. Non-Returnable Items</h3>
                <ul>
                  <li>All clearance or sale items are final sale and cannot be returned or refunded.</li>
                </ul>
              </section>

              <section>
                <h3>5. Delivery Delays</h3>
                <ul>
                  <li>If we are unable to deliver your order within 90 days from the purchase date, you may request a full refund.</li>
                </ul>
              </section>

              <section>
                <h3>6. Order Revocation Due to Non-Collection</h3>
                <ul>
                  <li>If your order arrives in the destination country and you do not collect it within 30 days, Mega Genie reserves the right to revoke/cancel your order without refund eligibility.</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
